/**
 * SubscriptionCard Component (September 2025)
 *
 * Displays subscription details with status indicators and management controls.
 * Shows renewal dates, cycle information, and cancel option for active subscriptions.
 */

import { useState } from 'react';

interface Subscription {
  id: string;
  amount: number;
  validityDays: number;
  status: 'active' | 'canceled' | 'paused' | 'expired';
  currentCycleStart: number;
  currentCycleEnd: number;
  nextRenewalAt: number;
  renewalCount?: number;
  createdAt?: number;
}

interface SubscriptionCardProps {
  subscription: Subscription;
  onCancel: (subscription: Subscription) => void;
  isCanceling: boolean;
}

export function SubscriptionCard({ subscription, onCancel, isCanceling }: SubscriptionCardProps) {
  const [showDetails, setShowDetails] = useState(false);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatRelativeDate = (timestamp: number) => {
    const now = Date.now();
    const diff = timestamp - now;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
      return `in ${days} day${days !== 1 ? 's' : ''}`;
    } else if (hours > 0) {
      return `in ${hours} hour${hours !== 1 ? 's' : ''}`;
    } else if (diff > 0) {
      return 'very soon';
    } else {
      return 'overdue';
    }
  };

  const statusColors = {
    active: 'bg-green-500/20 border-green-500 text-green-400',
    canceled: 'bg-red-500/20 border-red-500 text-red-400',
    paused: 'bg-yellow-500/20 border-yellow-500 text-yellow-400',
    expired: 'bg-gray-500/20 border-gray-500 text-gray-400',
  };

  const statusIcons = {
    active: '✓',
    canceled: '✕',
    paused: '⏸',
    expired: '⌛',
  };

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg p-6 shadow-lg hover:shadow-xl transition-shadow">
      {/* Header with Status */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="text-4xl">📅</div>
          <div>
            <h3 className="text-xl font-bold text-cyan-400">
              {subscription.amount} Credits
            </h3>
            <p className="text-sm text-slate-400">
              Every {subscription.validityDays} days
            </p>
          </div>
        </div>
        <div
          className={`px-3 py-1 rounded-full text-xs font-semibold border ${
            statusColors[subscription.status]
          }`}
        >
          {statusIcons[subscription.status]} {subscription.status.toUpperCase()}
        </div>
      </div>

      {/* Subscription Details */}
      <div className="space-y-3 mb-4">
        {subscription.status === 'active' && (
          <>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Next Renewal</span>
              <span className="text-cyan-400 font-semibold">
                {formatRelativeDate(subscription.nextRenewalAt)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-slate-400">Renewal Date</span>
              <span className="text-slate-300">{formatDate(subscription.nextRenewalAt)}</span>
            </div>
          </>
        )}

        {subscription.status === 'canceled' && (
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Cycle Ends</span>
            <span className="text-slate-300">{formatDate(subscription.currentCycleEnd)}</span>
          </div>
        )}

        <div className="flex justify-between text-sm">
          <span className="text-slate-400">Renewals</span>
          <span className="text-slate-300">{subscription.renewalCount || 0} times</span>
        </div>
      </div>

      {/* Show More Details Toggle */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="text-sm text-cyan-400 hover:text-cyan-300 mb-4 transition-colors"
      >
        {showDetails ? '▼ Hide Details' : '▶ Show Details'}
      </button>

      {/* Extended Details */}
      {showDetails && (
        <div className="bg-slate-900 rounded-lg p-4 mb-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-400">Subscription ID</span>
            <span className="text-slate-300 font-mono text-xs">{subscription.id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Cycle Start</span>
            <span className="text-slate-300">{formatDate(subscription.currentCycleStart)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Cycle End</span>
            <span className="text-slate-300">{formatDate(subscription.currentCycleEnd)}</span>
          </div>
          {subscription.createdAt && (
            <div className="flex justify-between">
              <span className="text-slate-400">Created</span>
              <span className="text-slate-300">{formatDate(subscription.createdAt)}</span>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {subscription.status === 'active' && (
        <button
          onClick={() => onCancel(subscription)}
          disabled={isCanceling}
          className="w-full px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/50 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isCanceling ? 'Canceling...' : 'Cancel Subscription'}
        </button>
      )}

      {subscription.status === 'canceled' && (
        <div className="text-center text-sm text-slate-400 italic">
          Credits remain active until cycle ends
        </div>
      )}
    </div>
  );
}
